import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { 
  Camera,
  User,
  Mail,
  Key,
  CheckCircle,
  AlertTriangle,
  Eye,
  EyeOff,
  Upload,
  Loader2,
  Edit2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import PasswordChangeModal from '../modals/PasswordChangeModal';
import ChangeEmailModal from '../modals/ChangeEmailModal';

const ProfileTab = ({ user }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [isAvatarHovered, setIsAvatarHovered] = useState(false);
  
  const [profileData, setProfileData] = useState({
    full_name: user.full_name || user.name || '',
    email: user.email || ''
  });

  const fileInputRef = useRef(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/settings/profile`,
        { full_name: profileData.full_name },
        { withCredentials: true }
      );

      toast.success('✓ Profile updated successfully!', {
        duration: 3000,
        position: 'top-center'
      });

      // Refresh page to update user context
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error('Profile update error:', error);
      const errorMessage = error.response?.data?.detail || 'Profile update failed';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = async (newEmail) => {
    setIsLoading(true);
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/settings/profile`,
        { 
          full_name: profileData.full_name,
          email: newEmail 
        },
        { withCredentials: true }
      );

      toast.success('✓ Verification email sent! Please check your inbox.', {
        duration: 5000,
        position: 'top-center'
      });

      setProfileData({ ...profileData, email: newEmail });

    } catch (error) {
      console.error('Email change error:', error);
      const errorMessage = error.response?.data?.detail || 'Email change failed';
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Password change handled by modal

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }

    setAvatarUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/settings/profile/avatar`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true
        }
      );

      toast.success('✓ Avatar updated successfully!', {
        duration: 3000,
        position: 'top-center'
      });
      
      // Refresh page to show new avatar
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error('Avatar upload error:', error);
      const errorMessage = error.response?.data?.detail || 'Avatar upload failed';
      toast.error(errorMessage);
    } finally {
      setAvatarUploading(false);
    }
  };

  // Helper function to get user initials for placeholder avatar
  const getUserInitials = () => {
    const name = user.full_name || user.name || user.email;
    return name.charAt(0).toUpperCase();
  };

  // Generate consistent color based on user email
  const getAvatarColor = () => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-yellow-500',
      'bg-teal-500'
    ];
    const email = user.email || '';
    const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  return (
    <div className="space-y-6">
      {/* Avatar Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Camera className="w-5 h-5 text-blue-600" />
            <span>Profile Picture</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-6">
            <div 
              className="relative cursor-pointer group"
              onMouseEnter={() => setIsAvatarHovered(true)}
              onMouseLeave={() => setIsAvatarHovered(false)}
              onClick={() => fileInputRef.current?.click()}
            >
              {user.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt="Avatar"
                  className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                />
              ) : (
                <div className={`w-20 h-20 ${getAvatarColor()} rounded-full flex items-center justify-center border-2 border-gray-200`}>
                  <span className="text-white text-2xl font-medium">
                    {getUserInitials()}
                  </span>
                </div>
              )}
              
              {/* Hover overlay */}
              {isAvatarHovered && !avatarUploading && (
                <div className="absolute inset-0 bg-black bg-opacity-60 rounded-full flex items-center justify-center transition-opacity">
                  <Camera className="w-6 h-6 text-white" />
                </div>
              )}
              
              {avatarUploading && (
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <h3 className="font-medium text-gray-900">Upload new picture</h3>
              <p className="text-sm text-gray-500">
                JPG, PNG, or WEBP. Maximum 5MB.
              </p>
              <div className="flex space-x-3">
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={avatarUploading}
                  variant="outline"
                  size="sm"
                  className="flex items-center space-x-2"
                >
                  <Upload className="w-4 h-4" />
                  <span>Choose file</span>
                </Button>
                {user.avatar_url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                  >
                    Remove
                  </Button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                className="hidden"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Personal Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <User className="w-5 h-5 text-blue-600" />
            <span>Personal Information</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  type="text"
                  value={profileData.full_name}
                  onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                  placeholder="Enter your full name"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="email" className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span>Email Address</span>
                    {user.email_verified ? (
                      <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    ) : (
                      <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 text-xs">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Unverified
                      </Badge>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowEmailModal(true)}
                    className="h-auto p-0 text-blue-600 hover:text-blue-700 hover:bg-transparent"
                  >
                    <Edit2 className="w-3 h-3 mr-1" />
                    Change Email
                  </Button>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={profileData.email}
                  disabled
                  className="mt-1 bg-gray-50 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Click "Change Email" to update your email address
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setProfileData({
                    full_name: user.full_name || user.name || '',
                    email: user.email || ''
                  });
                }}
              >
                Reset
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="flex items-center space-x-2"
              >
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                <span>Save Changes</span>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Password Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Key className="w-5 h-5 text-blue-600" />
              <span>Password</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPasswordModal(true)}
              className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
            >
              Change Password
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600">
            Keep your account secure by using a strong, unique password. 
            We recommend changing your password periodically.
          </p>
          <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm text-gray-700">
              • Password must be at least 8 characters<br />
              • Include uppercase and lowercase letters<br />
              • Include at least one number and special character
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Modals */}
      <PasswordChangeModal 
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        userId={user.id}
      />
      
      <ChangeEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        currentEmail={user.email}
        onEmailChange={handleEmailChange}
      />
    </div>
  );
};

export default ProfileTab;